import {
  ACCESS_TOKEN_MAX_AGE_SECOND,
  REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import { datadogRum } from '@datadog/browser-rum';
import { SignInResponse } from '@models/auth';
import axios, {
  AxiosError,
  AxiosInstance, AxiosRequestConfig, AxiosRequestTransformer, AxiosResponse, InternalAxiosRequestConfig,
} from 'axios';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

export interface HttpClient extends AxiosInstance {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

const isPlainObject = (v: unknown) => Object.prototype.toString.call(v) === '[object Object]';

const dateTransformer: AxiosRequestTransformer = (data: any) => {
  if (data instanceof Date) {
    return dayjs(data).toISOString();
  }
  if (Array.isArray(data)) {
    // @ts-ignore
    return data.map((val: any) => dateTransformer(val));
  }
  if (isPlainObject(data)) {
    return Object.fromEntries(
      // @ts-ignore
      Object.entries(data).map(([key, val]) => [key, dateTransformer(val)]),
    );
  }
  return data;
};

const instance: HttpClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  // @ts-ignore
  transformRequest: [dateTransformer].concat(axios.defaults.transformRequest),
  paramsSerializer: {
    indexes: null,
  },
});

let isRefreshing = false;
let isUnAuthorized = false;
let reIssueFailed = false;

function reissueToken(refreshToken: string): Promise<AxiosResponse<SignInResponse>> {
  return axios.post(
    `${import.meta.env.VITE_API_URL}/auth/token/refresh`,
    { refreshToken },
  );
}

async function handlerRequest(config: InternalAxiosRequestConfig<any>) {
  config.headers['Waldlust-Project-ID'] = cookies.get('projectId');
  //  요청 시 토큰 추가
  let accessToken = cookies.get('accessToken');
  if (!accessToken || isUnAuthorized) {
    const refreshToken = cookies.get('refreshToken');
    if (refreshToken && (!isRefreshing)) {
      isRefreshing = true;
      try {
        const res = (await reissueToken(refreshToken)).data;
        accessToken = res.token;
        cookies.set('accessToken', accessToken, {
          path: '/',
          maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
        });
        cookies.set('refreshToken', res.refreshToken, {
          path: '/',
          maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
        });
        isUnAuthorized = false;
        reIssueFailed = false;
      } catch (e) {
        cookies.remove('accessToken', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        console.error('인증 오류 발생', e);
        datadogRum.addAction('refreshTokenFailed', {
          message: JSON.stringify(e),
        });
        reIssueFailed = true;
      }
      isRefreshing = false;
    }
  }
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
}

instance.interceptors.response.use(
  (res) => res.data,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const projectId = cookies.get('projectId');
      if (projectId) {
        cookies.remove('accessToken', { path: '/' });
        isUnAuthorized = true;
        if (reIssueFailed) {
          cookies.remove('refreshToken', { path: '/' });
        }
      }
    }
    return Promise.reject(error);
  },
);
instance.interceptors.request.use(handlerRequest);

export default instance;
